/**
 * PromoteModal — Tier picker + benefits display + Stripe checkout.
 *
 * Buyers see EXACTLY what they're paying for at each tier (data driven from
 * GET /api/promote/tiers/benefits?promote_type={t}). Each tier inherits all
 * lower tiers' benefits (cumulative stack). Stripe LIVE mode — never simulate
 * past the redirect.
 *
 * Honors the 48-hour public-visibility window: when a brand-new track or
 * album is promoted before its content-wait expires, the activation defers
 * to the latest pending public_at. The buyer is informed inline so they
 * understand why the boost won't start the second they pay.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  TrendingUp, X, Loader2, DollarSign, Check, Sparkles,
  Music, Video, User, Calendar, Disc, Zap, Clock, ChevronDown, ChevronUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_ICONS = { track: Music, ai_track: Music, video: Video, profile: User, album: Disc, event: Calendar };
const TYPE_LABEL = {
  track: 'Track', ai_track: 'AI Track', video: 'Video',
  profile: 'Profile', album: 'Album', event: 'Event'
};

export default function PromoteModal({ isOpen, onClose, promoteType, targetId, targetTitle }) {
  const { token, user } = useAuth();
  const [selectedTier, setSelectedTier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [credit, setCredit] = useState({ balance: 0, next_expiry: null });
  // Benefits matrix loaded once per content type from the backend.
  const [benefitsByTier, setBenefitsByTier] = useState({});
  const [tiers, setTiers] = useState([]);
  const [loadingBenefits, setLoadingBenefits] = useState(false);
  // Per-tier expand toggle on mobile so the modal isn't a wall of text.
  const [expandedTier, setExpandedTier] = useState(null);

  // Fetch benefits matrix when modal opens for this content type.
  useEffect(() => {
    if (!isOpen || !promoteType) return;
    setLoadingBenefits(true);
    setSelectedTier(null);
    axios.get(`${API}/promote/tiers/benefits`, { params: { promote_type: promoteType } })
      .then(r => {
        const byTier = r.data?.by_tier || {};
        setBenefitsByTier(byTier);
        // Tier order from the response keys (boost_24h → boost_30d).
        const order = ['boost_24h', 'boost_3d', 'boost_7d', 'boost_30d'];
        setTiers(order.filter(k => byTier[k]).map(k => byTier[k].tier_meta));
      })
      .catch(err => {
        console.error('[Promote] Failed to load benefits:', err);
        toast.error('Could not load promotion plans');
      })
      .finally(() => setLoadingBenefits(false));
  }, [isOpen, promoteType]);

  // Fetch promotion-credit balance.
  useEffect(() => {
    if (!isOpen || !token) return;
    axios.get(`${API}/promotion-credits/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCredit(r.data))
      .catch(() => setCredit({ balance: 0, next_expiry: null }));
  }, [isOpen, token]);

  const isCreator = useMemo(
    () => user && (user.is_artist || user.is_producer || user.is_ai_artist || user.is_dj || user.is_promoter || user.is_admin),
    [user]
  );

  const handlePromote = async () => {
    if (!token) { toast.error('Please log in to promote'); return; }
    if (!isCreator) { toast.error('Fan accounts cannot promote. Upgrade to a creator account.'); return; }
    if (!selectedTier) { toast.error('Select a promotion plan'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/promote`, {
        tier: selectedTier,
        items: [{ promote_type: promoteType, target_id: targetId }],
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.status === 'active' || res.data.status === 'scheduled') {
        toast.success(
          res.data.status === 'scheduled'
            ? 'Promotion scheduled — starts when the 48h public-window opens.'
            : 'Promotion activated!'
        );
        onClose();
      } else {
        toast.error('Payment setup failed. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promotion');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  const TypeIcon = TYPE_ICONS[promoteType] || Music;
  const typeLabel = TYPE_LABEL[promoteType] || promoteType;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="promote-modal"
    >
      <div
        className="bg-[#0a0a0a] border border-[#1a1a1a] w-full max-w-3xl my-8 overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─────────── Header ─────────── */}
        <div className="flex items-center justify-between p-4 border-b border-[#1a1a1a] flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-pink-500/15 border border-pink-500/40 flex items-center justify-center flex-shrink-0">
              <TypeIcon size={18} className="text-pink-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-['Chakra_Petch'] text-lg uppercase tracking-wider flex items-center gap-2">
                <TrendingUp size={16} className="text-pink-400" /> Promote {typeLabel}
              </h3>
              <p className="text-[#888] font-mono text-xs truncate">{targetTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#666] hover:text-white flex-shrink-0"
            data-testid="promote-modal-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* ─────────── Credit banner ─────────── */}
        {credit.balance > 0 && token && isCreator && (
          <div
            className="px-4 py-2.5 border-b border-[#1a1a1a] bg-gradient-to-r from-[#FFDA33]/15 to-[#00ff88]/10 flex items-center justify-between flex-shrink-0"
            data-testid="promotion-credit-banner"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-[#FFDA33]" />
              <span className="text-white font-mono text-xs">
                <span className="text-[#FFDA33] font-bold">${credit.balance.toFixed(2)}</span> promotion credit applied at checkout
              </span>
            </div>
            {credit.next_expiry && (
              <span className="text-[10px] font-mono text-[#888]">exp. {credit.next_expiry.substring(0, 10)}</span>
            )}
          </div>
        )}

        {/* ─────────── 48h-wait notice ─────────── */}
        {(promoteType === 'track' || promoteType === 'ai_track' || promoteType === 'album' || promoteType === 'video') && (
          <div className="px-4 py-2.5 border-b border-[#1a1a1a] bg-[#FFDA33]/5 flex items-start gap-2 flex-shrink-0">
            <Clock size={14} className="text-[#FFDA33] flex-shrink-0 mt-0.5" />
            <p className="text-[#aaa] font-mono text-[11px] leading-relaxed">
              <span className="text-[#FFDA33]">Heads up:</span> if your {typeLabel.toLowerCase()} is still in its 48-hour public-visibility window,
              the promotion auto-starts the moment that window opens — no time wasted.
            </p>
          </div>
        )}

        {/* ─────────── Body ─────────── */}
        {!isCreator && token ? (
          <div className="p-8 text-center">
            <p className="text-[#888] font-mono text-sm">Fan accounts cannot promote.</p>
            <p className="text-[#555] text-xs mt-1">Upgrade to a creator account (artist / producer / AI artist) to use promotions.</p>
          </div>
        ) : !token ? (
          <div className="p-8 text-center">
            <p className="text-[#888] font-mono text-sm">Please log in to promote content.</p>
          </div>
        ) : loadingBenefits ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 size={24} className="animate-spin text-pink-400" />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1">
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="promote-tier-grid">
              {tiers.map((t) => {
                const isSelected = selectedTier === t.key;
                const benefits = benefitsByTier[t.key]?.cumulative_benefits || [];
                const isExpanded = expandedTier === t.key;
                const visible = isExpanded ? benefits : benefits.slice(0, 3);
                return (
                  <button
                    key={t.key}
                    onClick={() => setSelectedTier(t.key)}
                    className={`text-left p-4 border transition-all relative ${
                      isSelected
                        ? 'border-2 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
                        : 'border-[#1a1a1a] hover:border-[#333]'
                    }`}
                    style={isSelected ? { borderColor: t.color, backgroundColor: `${t.color}11` } : {}}
                    data-testid={`modal-tier-${t.key}`}
                  >
                    {/* Tier header */}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-['Chakra_Petch'] text-sm uppercase tracking-widest" style={{ color: t.color }}>
                          {t.tier_name}
                        </p>
                        <p className="text-[#888] font-mono text-[11px]">{t.days} day{t.days > 1 ? 's' : ''}{t.max_types > 1 ? ` · up to ${t.max_types} items` : ''}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {isSelected && <Check size={14} className="text-green-400" />}
                        <span className="font-['Chakra_Petch'] text-2xl" style={{ color: t.color }}>${t.price}</span>
                      </div>
                    </div>

                    {/* Benefits list (cumulative — inherits everything from lower tiers) */}
                    <ul className="space-y-1.5">
                      {visible.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[#ddd] text-[12px] leading-snug">
                          <Check size={11} className="flex-shrink-0 mt-1" style={{ color: t.color }} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {benefits.length > 3 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpandedTier(isExpanded ? null : t.key); }}
                        className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#888] hover:text-white inline-flex items-center gap-1"
                        data-testid={`modal-tier-${t.key}-toggle`}
                      >
                        {isExpanded ? <>Show less <ChevronUp size={10}/></> : <>+{benefits.length - 3} more <ChevronDown size={10}/></>}
                      </button>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ─────────── Action ─────────── */}
        {token && isCreator && !loadingBenefits && (
          <div className="p-4 border-t border-[#1a1a1a] flex-shrink-0">
            <Button
              onClick={handlePromote}
              disabled={!selectedTier || submitting}
              className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-['Chakra_Petch'] uppercase tracking-wider py-5 rounded-none"
              data-testid="modal-promote-btn"
            >
              {submitting ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Zap size={16} className="mr-2" />
              )}
              {selectedTier
                ? `Pay $${tiers.find(t => t.key === selectedTier)?.price} · Promote ${typeLabel}`
                : 'Select a plan'}
            </Button>
            <p className="text-[10px] font-mono text-[#666] text-center mt-2">
              Stripe-secured · No subscription · Single one-time charge
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * PromoteButton — Small inline button to trigger the promote modal.
 */
export function PromoteButton({ promoteType, targetId, targetTitle, size = 'sm', variant = 'icon', className = '' }) {
  const [showModal, setShowModal] = useState(false);

  if (variant === 'icon') {
    return (
      <>
        <button
          onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
          className={`text-[#666] hover:text-pink-400 transition-colors ${className}`}
          title="Promote"
          data-testid={`promote-btn-${promoteType}-${targetId}`}
        >
          <TrendingUp size={size === 'sm' ? 14 : 18} />
        </button>
        <PromoteModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          promoteType={promoteType}
          targetId={targetId}
          targetTitle={targetTitle}
        />
      </>
    );
  }

  return (
    <>
      <Button
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
        variant="outline"
        className={`border-pink-500/30 text-pink-400 hover:bg-pink-500/10 font-mono ${
          size === 'sm' ? 'text-xs px-3 py-1.5 h-auto' : 'text-sm'
        } ${className}`}
        data-testid={`promote-btn-${promoteType}-${targetId}`}
      >
        <TrendingUp size={size === 'sm' ? 12 : 16} className="mr-1" />
        Promote
      </Button>
      <PromoteModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        promoteType={promoteType}
        targetId={targetId}
        targetTitle={targetTitle}
      />
    </>
  );
}
